import { andList } from './pageChecks';

/**
 * What Design says after a template is put on the working theme, and what
 * Discard says about the drafts a template added (backlogs dd3fa9f5,
 * 70a50c12). One toast, in the design's words: "Working theme: Magazine.
 * Added Story, About, Tips to Pages as drafts."
 */

export interface StarterResult {
  pages?: Array<{ title?: string | null; kept?: boolean }>;
  forms?: Array<{ name?: string | null; kept?: boolean }>;
  /** Parts of the site document Look and pages replaced ('navigation', 'collections'). */
  replaced?: string[];
}

/** The site's starter record of what templates added since the last publish. */
export interface TemplateAdded {
  pages?: Array<{ id: string; title?: string | null; template?: string }>;
  forms?: Array<{ id: string; name?: string | null; template?: string }>;
}

export interface TemplateDrafts {
  removed: Array<{ kind: 'page' | 'form'; title?: string | null }>;
  kept: Array<{ kind: 'page' | 'form'; title?: string | null }>;
}

const titles = (list: Array<{ title?: string | null; name?: string | null }>, fallback: string) =>
  list.map((x) => x.title || x.name || fallback);

/** Use the look: tokens only. */
export const lookText = (template: string) =>
  `Working theme: ${template}. Every page and article stays as it is.`;

/** Look and pages: every object it added or kept, by name. */
export function lookPagesText(template: string, r: StarterResult): string {
  const pages = r.pages || [];
  const forms = r.forms || [];
  const newPages = titles(
    pages.filter((p) => !p.kept),
    'Untitled page'
  );
  const newForms = titles(
    forms.filter((f) => !f.kept),
    'Untitled form'
  );
  const keptPages = titles(
    pages.filter((p) => p.kept),
    'Untitled page'
  );
  const out = [`Working theme: ${template}.`];
  const drafts = (n: number) => (n === 1 ? 'a draft' : 'drafts');
  if (newPages.length)
    out.push(`Added ${andList(newPages)} to Pages as ${drafts(newPages.length)}.`);
  if (newForms.length) {
    const the = /^the\s/i.test(newForms[0]) ? '' : 'the ';
    const forms = `${the}${andList(newForms)} ${newForms.length === 1 ? 'form' : 'forms'}`;
    out.push(
      newPages.length ? `Added ${forms} too.` : `Added ${forms} as ${drafts(newForms.length)}.`
    );
  }
  if (keptPages.length) {
    const added = newPages.length + newForms.length > 0;
    out.push(
      `${added ? 'Kept' : 'Nothing new to add: kept'} your ${andList(keptPages)} ${keptPages.length === 1 ? 'page' : 'pages'} as ${keptPages.length === 1 ? 'it is' : 'they are'}.`
    );
  }
  if ((r.replaced || []).includes('navigation')) out.push(`The menu is ${template}’s.`);
  return out.join(' ');
}

/** After Discard: what was taken back, and what stays (edited, or on a page that stays). */
export function discardResultText(liveVersion: number, drafts?: TemplateDrafts | null): string {
  const out = [
    `Working changes discarded. Readers still see ${liveVersion ? `theme v${liveVersion}` : 'the default theme'}.`,
  ];
  const said = (list: TemplateDrafts['removed']) =>
    andList(list.map((x) => `${x.title || 'Untitled'} ${x.kind === 'form' ? 'form' : 'page'}`));
  if (drafts?.removed.length) out.push(`Removed the template’s drafts: ${said(drafts.removed)}.`);
  if (drafts?.kept.length) {
    const one = drafts.kept.length === 1;
    out.push(
      `${/^the\s/i.test(drafts.kept[0].title || '') ? '' : 'The '}${said(drafts.kept)} ${one ? 'stays as a draft' : 'stay as drafts'} (edited since, or on a page that stays). Delete ${one ? 'it' : 'them'} from Pages or Forms if you don’t want ${one ? 'it' : 'them'}.`
    );
  }
  return out.join(' ');
}

/**
 * Look and pages' confirm (design review round 27 §2): what the template
 * brings, row by row, before anything is added. The footer never carries the
 * person's sign-in email: the site's contact email, or none (§7).
 */
export function lookPagesConfirm({
  template,
  siteName,
  pages,
  menu,
  forms,
  havePaths,
  haveForms,
  currentMenu,
  contactEmail,
  year = new Date().getFullYear(),
}: {
  template: string;
  siteName: string;
  pages: Array<{ title: string; slug: string; home?: boolean }>;
  menu: string[];
  forms: Array<{ name: string; purpose?: string }>;
  /** Addresses of the site's pages ("about", no slash). */
  havePaths: Set<string>;
  haveForms: Set<string>;
  currentMenu: string[];
  contactEmail?: string | null;
  year?: number;
}) {
  const fresh = pages.filter((p) => !havePaths.has(p.slug));
  const kept = pages.filter((p) => havePaths.has(p.slug));
  const drafts = fresh.length
    ? `${andList(fresh.map((p) => p.title))}.${kept.length ? ' Pages you already have are kept.' : ''}`
    : `None: you already have ${andList(kept.map((p) => p.title))}.`;
  const leaving = currentMenu.filter((l) => !menu.includes(l));
  const menuText = [
    menu.length ? `Becomes ${menu.join(', ')}.` : '',
    leaving.length
      ? `${andList(leaving)} ${leaving.length === 1 ? 'leaves' : 'leave'} the menu. ${leaving.length === 1 ? 'Its page or section stays' : 'Their pages, sections and articles stay'}.`
      : '',
  ]
    .filter(Boolean)
    .join(' ');
  const newForms = forms.filter((f) => !haveForms.has(f.name));
  const formText = newForms
    .map((f) => `${f.name}, ${f.purpose === 'signup' ? 'a sign-up form' : 'a contact form'}`)
    .join('; ');
  const email = String(contactEmail || '').trim();
  const rows = [
    { label: 'Drafts', text: drafts },
    ...(menuText ? [{ label: 'Menu', text: menuText }] : []),
    ...(formText
      ? [{ label: newForms.length === 1 ? 'Form' : 'Forms', text: `${formText}, unpublished.` }]
      : []),
    { label: 'Filled in', text: `${siteName} and its line. The rest is marked as sample text.` },
    {
      label: 'Footer',
      text: email
        ? `© ${year} ${siteName} · ${email}. Your sign-in email is never added.`
        : `© ${year} ${siteName}. Your sign-in email is never added. To show an address, set a contact email in Site.`,
    },
  ];
  return {
    title: `Add the ${template} template’s pages to ${siteName}?`,
    body: `You get the ${template} look, plus its pages, menu and front page as unpublished drafts.`,
    rows,
    foot: 'Nothing is published. You check it all in the working copy bar first.',
  };
}
